import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { parseSignature } from "viem";
import { AppShell } from "@/components/AppShell";
import { governanceApi, type Proposal, type VotingMeta, ApiError } from "@/api/client";
import { keystore } from "@/keystore";

// Base Sepolia.
const CHAIN_ID = 84532;

/**
 * Governança: criar propostas, listar com estado/votos, e votar de forma
 * GASLESS — o sócio assina o voto (EIP-712) com a chave do navegador e o
 * sistema submete pagando o gas.
 */
export default function Proposals() {
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [meta, setMeta] = useState<VotingMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [desc, setDesc] = useState("");
  // Opções de voto. Começa com 2 (sim/não), o sócio pode adicionar até 10.
  const [options, setOptions] = useState<string[]>(["A favor", "Contra"]);
  const [busy, setBusy] = useState(false);

  function setOption(i: number, v: string) {
    setOptions((prev) => prev.map((o, idx) => (idx === i ? v : o)));
  }
  function addOption() {
    setOptions((prev) => (prev.length >= 10 ? prev : [...prev, ""]));
  }
  function removeOption(i: number) {
    setOptions((prev) => (prev.length <= 2 ? prev : prev.filter((_, idx) => idx !== i)));
  }

  function load() {
    governanceApi.list()
      .then((r) => setProposals(r.proposals))
      .catch((err) => setError(err instanceof ApiError ? err.message : err.message))
      .finally(() => setLoading(false));
    governanceApi.votingMeta().then(setMeta).catch(() => setMeta(null));
  }
  useEffect(load, []);

  const myAddr = keystore.getAddress()?.toLowerCase();
  const delegatedToSelf = meta && myAddr && meta.delegatee.toLowerCase() === myAddr;

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    const cleanOptions = options.map((o) => o.trim()).filter(Boolean);
    if (cleanOptions.length < 2) {
      setError("Defina pelo menos 2 opções de voto.");
      return;
    }
    setBusy(true); setError(null); setInfo(null);
    try {
      await governanceApi.create(desc.trim(), cleanOptions);
      setDesc("");
      setOptions(["A favor", "Contra"]);
      setInfo("Proposta criada! Assim que abrir (conforme o delay configurado), os sócios podem votar.");
      load();
    } catch (err: any) {
      setError(err instanceof ApiError ? err.message : err.message ?? "Erro");
    } finally { setBusy(false); }
  }

  async function onVote(proposalId: string, support: number) {
    if (!meta) return;
    setBusy(true); setError(null); setInfo(null);
    try {
      const signature = await keystore.signTypedData({
        domain: { name: meta.governorName, version: "1", chainId: CHAIN_ID, verifyingContract: meta.governor },
        types: { Ballot: [
          { name: "proposalId", type: "uint256" },
          { name: "support", type: "uint8" },
          { name: "voter", type: "address" },
          { name: "nonce", type: "uint256" },
        ] },
        primaryType: "Ballot",
        message: {
          proposalId: BigInt(proposalId),
          support,
          voter: keystore.getAddress()!,
          nonce: BigInt(meta.governorNonce),
        },
      });
      await governanceApi.vote(proposalId, support, signature);
      setInfo("Voto registrado on-chain!");
      load();
    } catch (err: any) {
      setError(err instanceof ApiError ? err.message : err.message ?? "Erro ao votar");
    } finally { setBusy(false); }
  }

  async function onDelegate() {
    if (!meta) return;
    setBusy(true); setError(null); setInfo(null);
    try {
      const expiry = Math.floor(Date.now() / 1000) + 3600;
      const signature = await keystore.signTypedData({
        domain: { name: meta.tokenName, version: "1", chainId: CHAIN_ID, verifyingContract: meta.token },
        types: { Delegation: [
          { name: "delegatee", type: "address" },
          { name: "nonce", type: "uint256" },
          { name: "expiry", type: "uint256" },
        ] },
        primaryType: "Delegation",
        message: {
          delegatee: keystore.getAddress()!,
          nonce: BigInt(meta.tokenNonce),
          expiry: BigInt(expiry),
        },
      });
      const sig = parseSignature(signature);
      const v = Number(sig.v ?? (sig.yParity + 27));
      await governanceApi.delegate(meta.tokenNonce, String(expiry), v, sig.r, sig.s);
      setInfo("Poder de voto ativado! Seus tokens na carteira agora contam.");
      governanceApi.votingMeta().then(setMeta).catch(() => undefined);
    } catch (err: any) {
      setError(err instanceof ApiError ? err.message : err.message ?? "Erro ao delegar");
    } finally { setBusy(false); }
  }

  function fmt(wei: string) {
    try { return (BigInt(wei) / 10n ** 18n).toLocaleString("pt-BR"); } catch { return wei; }
  }

  return (
    <AppShell>
      <div className="max-w-4xl mx-auto p-6">
        <Link to="/dashboard" className="text-sm underline text-app-muted hover:text-app-accent">← dashboard</Link>
        <h1 className="text-2xl font-bold my-4">Propostas</h1>

        {meta && (
          <div className="app-card p-4 mb-4 flex items-center justify-between gap-4">
            <div className="text-sm">
              <span className="text-app-muted">Seu poder de voto:</span>{" "}
              <strong>{fmt(meta.votingPower)}</strong>
              {!delegatedToSelf && (
                <p className="text-xs text-amber-600 mt-1">
                  Seus tokens na carteira ainda não contam — ative seu voto.
                </p>
              )}
            </div>
            {!delegatedToSelf && (
              <button onClick={onDelegate} disabled={busy}
                      className="bg-app-accent text-app-accent-text rounded px-4 py-2 text-sm font-medium disabled:opacity-50">
                Ativar meu voto
              </button>
            )}
          </div>
        )}

        <form onSubmit={onCreate} className="app-card p-4 mb-4">
          <h2 className="font-semibold mb-2">Nova proposta</h2>
          <textarea value={desc} onChange={(e) => setDesc(e.target.value)} required minLength={3}
                    className="w-full px-3 py-2 text-sm" rows={3}
                    placeholder="Descreva a proposta a ser votada pela maioria…" />

          <label className="block text-sm font-medium mt-3 mb-1">Opções de voto</label>
          <p className="text-xs text-app-muted mb-2">
            Quem tiver mais votos vence (maioria). Mínimo 2, máximo 10. Abstenção é automática.
          </p>
          <div className="space-y-2">
            {options.map((opt, i) => (
              <div key={i} className="flex gap-2 items-center">
                <span className="text-xs text-app-muted w-5 text-right">{i + 1}.</span>
                <input value={opt} onChange={(e) => setOption(i, e.target.value)}
                       className="flex-1 px-3 py-2 text-sm"
                       placeholder={`Opção ${i + 1}`} maxLength={80} />
                {options.length > 2 && (
                  <button type="button" onClick={() => removeOption(i)}
                          className="text-app-muted hover:text-red-500 px-2" title="Remover">✕</button>
                )}
              </div>
            ))}
          </div>
          {options.length < 10 && (
            <button type="button" onClick={addOption}
                    className="mt-2 text-xs text-app-accent underline">+ adicionar opção</button>
          )}

          <div className="mt-3">
            <button type="submit" disabled={busy || desc.trim().length < 3}
                    className="bg-app-accent text-app-accent-text rounded px-4 py-2 text-sm font-medium disabled:opacity-50">
              {busy ? "..." : "Criar proposta"}
            </button>
          </div>
        </form>

        {info && <div className="bg-green-500/10 border border-green-500/40 rounded p-3 text-sm text-green-600 mb-3">{info}</div>}
        {error && <div className="bg-red-500/10 border border-red-500/40 rounded p-3 text-sm text-red-500 mb-3">{error}</div>}
        {loading && <p className="text-sm text-app-muted">Carregando...</p>}

        {!loading && proposals.length === 0 && (
          <div className="app-card border-app-border p-8 text-center text-app-muted text-sm">
            Nenhuma proposta ainda. Crie a primeira acima.
          </div>
        )}

        <div className="space-y-3">
          {proposals.map((p) => {
            const optVotes = p.options.map((o) => Number(fmt(o.votes).replace(/\D/g, "")) || 0);
            const abst = Number(fmt(p.abstainVotes).replace(/\D/g, "")) || 0;
            const total = optVotes.reduce((s, n) => s + n, 0) + abst || 1;
            const maxVotes = Math.max(0, ...optVotes);
            const canVote = p.state === "active";
            const palette = ["bg-green-500", "bg-blue-500", "bg-purple-500", "bg-amber-500", "bg-pink-500", "bg-teal-500", "bg-orange-500", "bg-indigo-500", "bg-rose-500", "bg-cyan-500"];
            return (
              <div key={p.proposalId} className="app-card p-5">
                <div className="flex justify-between items-start mb-2 gap-3">
                  <h3 className="font-semibold">{p.description || "(sem descrição)"}</h3>
                  <span className="text-xs px-2 py-0.5 rounded bg-app-surface-2 text-app-muted whitespace-nowrap">{p.state}</span>
                </div>
                <p className="text-xs text-app-muted mb-3">
                  {p.proposalId.slice(0, 10)}… · por {p.proposer ? `${p.proposer.slice(0, 8)}…` : "—"}
                </p>
                <div className="space-y-1 text-xs mb-3">
                  {p.options.map((o, i) => (
                    <Bar key={i} label={o.label} n={optVotes[i]} total={total}
                         color={palette[i % palette.length]}
                         leading={canVote === false && optVotes[i] === maxVotes && maxVotes > 0} />
                  ))}
                  <Bar label="Abstenção" n={abst} total={total} color="bg-app-muted" />
                </div>
                <div className="flex gap-2 flex-wrap">
                  {p.options.map((o, i) => (
                    <VoteBtn key={i} label={o.label} disabled={!canVote || busy}
                             onClick={() => onVote(p.proposalId, i)}
                             cls="bg-app-surface-2 border-app-border hover:bg-app-border" />
                  ))}
                  <VoteBtn label="− Abstenção" disabled={!canVote || busy}
                           onClick={() => onVote(p.proposalId, 255)}
                           cls="bg-app-surface-2 border-app-border text-app-muted" />
                </div>
                {!canVote && <p className="text-xs text-app-muted mt-2">Votação {p.state === "pending" ? "ainda não abriu" : "encerrada"}.</p>}
              </div>
            );
          })}
        </div>
      </div>
    </AppShell>
  );
}

function Bar({ label, n, total, color, leading }: { label: string; n: number; total: number; color: string; leading?: boolean }) {
  return (
    <div className="flex gap-1 items-center">
      <span className={`w-24 truncate ${leading ? "font-semibold text-app-text" : "text-app-muted"}`} title={label}>
        {leading ? "🏆 " : ""}{label}:
      </span>
      <div className="flex-1 bg-app-surface-2 rounded h-2 overflow-hidden">
        <div className={`${color} h-full`} style={{ width: `${(n / total) * 100}%` }} />
      </div>
      <span className="w-20 text-right">{n.toLocaleString("pt-BR")}</span>
    </div>
  );
}

function VoteBtn({ label, onClick, disabled, cls }: { label: string; onClick: () => void; disabled: boolean; cls: string }) {
  return (
    <button onClick={onClick} disabled={disabled}
            className={`flex-1 border rounded py-2 text-sm disabled:opacity-40 disabled:cursor-not-allowed ${cls}`}>
      {label}
    </button>
  );
}
